import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { akEditorSelectedNodeClassName } from '@atlaskit/editor-shared-styles/constants';
import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { NextEditorPlugin } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { Node as ProseMirrorNode } from '@atlaskit/editor-prosemirror/model';
import { PluginKey, TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

const FLOATING_TABLE_OF_CONTENTS = 'floatingTableOfContents';

const SELECT_HEADING_META_TYPE = 'selectHeading';
const CLEAR_SELECTED_HEADING_META_TYPE = 'clearSelectedHeading';

type PluginMeta =
	| {
			type: typeof SELECT_HEADING_META_TYPE;
			position: number;
	  }
	| {
			type: typeof CLEAR_SELECTED_HEADING_META_TYPE;
	  };

type FloatingTableOfContentsPluginState = {
	decorations: DecorationSet;
	hasHeadings: boolean;
};

const pluginKey = new PluginKey<FloatingTableOfContentsPluginState>(FLOATING_TABLE_OF_CONTENTS);

const addChildNodesToVisit = (node: ProseMirrorNode, nodesToVisit: ProseMirrorNode[]): void => {
	for (let index = node.childCount - 1; index >= 0; index--) {
		nodesToVisit.push(node.child(index));
	}
};

const nodeHasHeading = (node: ProseMirrorNode): boolean => {
	const nodesToVisit: ProseMirrorNode[] = [node];

	while (nodesToVisit.length > 0) {
		const currentNode = nodesToVisit.pop();
		if (!currentNode) {
			continue;
		}

		if (currentNode.type.name === 'heading') {
			return true;
		}

		addChildNodesToVisit(currentNode, nodesToVisit);
	}

	return false;
};

const documentHasHeadings = (state: EditorState): boolean => nodeHasHeading(state.doc);

const getPluginMeta = (tr: Transaction): PluginMeta | undefined => {
	const meta: PluginMeta | undefined = tr.getMeta(pluginKey);

	if (!meta) {
		return undefined;
	}

	switch (meta.type) {
		case CLEAR_SELECTED_HEADING_META_TYPE:
			return { type: CLEAR_SELECTED_HEADING_META_TYPE };
		case SELECT_HEADING_META_TYPE:
			return { type: SELECT_HEADING_META_TYPE, position: meta.position };
		default:
			return undefined;
	}
};

const createSelectedHeadingDecorations = (state: EditorState, position: number): DecorationSet => {
	const heading = state.doc.nodeAt(position);
	if (heading?.type.name !== 'heading') {
		return DecorationSet.empty;
	}

	return DecorationSet.create(state.doc, [
		Decoration.node(position, position + heading.nodeSize, {
			class: akEditorSelectedNodeClassName,
		}),
	]);
};

const applyPluginState = (
	tr: Transaction,
	pluginState: FloatingTableOfContentsPluginState,
	_oldState: EditorState,
	newState: EditorState,
): FloatingTableOfContentsPluginState => {
	const meta = getPluginMeta(tr);
	let decorations: DecorationSet;

	switch (meta?.type) {
		case SELECT_HEADING_META_TYPE:
			decorations = createSelectedHeadingDecorations(newState, meta.position);
			break;
		case CLEAR_SELECTED_HEADING_META_TYPE:
			decorations = DecorationSet.empty;
			break;
		default:
			decorations = tr.docChanged ? DecorationSet.empty : pluginState.decorations;
	}

	return {
		decorations,
		hasHeadings: tr.docChanged ? documentHasHeadings(newState) : pluginState.hasHeadings,
	};
};

const getDecorations = (state: EditorState): DecorationSet =>
	pluginKey.getState(state)?.decorations ?? DecorationSet.empty;

const handleEditorClick = (view: EditorView): boolean => {
	const decorations = pluginKey.getState(view.state)?.decorations;
	if (decorations && decorations !== DecorationSet.empty) {
		const clearSelectedHeadingMeta: PluginMeta = {
			type: CLEAR_SELECTED_HEADING_META_TYPE,
		};
		view.dispatch(view.state.tr.setMeta(pluginKey, clearSelectedHeadingMeta));
	}

	return false;
};

const selectHeading =
	(position: number): EditorCommand =>
	({ tr }) => {
		const heading = tr.doc.nodeAt(position);
		const selectionPosition = position + 1;
		if (heading?.type.name !== 'heading' || selectionPosition > tr.doc.content.size) {
			return null;
		}

		const selectHeadingMeta: PluginMeta = {
			type: SELECT_HEADING_META_TYPE,
			position,
		};

		return tr
			.setSelection(TextSelection.create(tr.doc, selectionPosition))
			.setMeta(pluginKey, selectHeadingMeta)
			.setMeta('scrollIntoView', false);
	};

export type FloatingTableOfContentsPlugin = NextEditorPlugin<
	typeof FLOATING_TABLE_OF_CONTENTS,
	{
		commands: {
			selectHeading: (position: number) => EditorCommand;
		};
		sharedState: { hasHeadings: boolean } | undefined;
	}
>;

export const floatingTableOfContentsPlugin: FloatingTableOfContentsPlugin = () => ({
	name: FLOATING_TABLE_OF_CONTENTS,
	commands: {
		selectHeading,
	},
	getSharedState(editorState) {
		if (!editorState) {
			return undefined;
		}

		const pluginState = pluginKey.getState(editorState);
		return pluginState ? { hasHeadings: pluginState.hasHeadings } : undefined;
	},
	pmPlugins() {
		return [
			{
				name: 'floatingTableOfContentsPlugin',
				plugin: () =>
					new SafePlugin<FloatingTableOfContentsPluginState>({
						key: pluginKey,
						state: {
							init: (_config: unknown, state: EditorState) => ({
								decorations: DecorationSet.empty,
								hasHeadings: documentHasHeadings(state),
							}),
							apply: applyPluginState,
						},
						props: {
							decorations: getDecorations,
							handleDOMEvents: {
								click: handleEditorClick,
							},
						},
					}),
			},
		];
	},
});
